import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Search, Plus, ChevronDown, ChevronUp, Trash2, Edit3, Save, X, AlertCircle } from 'lucide-react';

const CATEGORIES = [
  'Treatment Stock',
  'Clinic Consumables',
  'Emergency Supplies',
  'Back Bar',
  'Office Consumables',
  'Front of House',
  'Janitorial',
];

const VAT_RATES = ['0', '5', '20'];

const empty = {
  name: '', category: 'Clinic Consumables', supplier: '', accountNo: '',
  unit: '', costPerUnit: '', vatRate: '20',
  currentQty: '', minQty: '', expiryDate: '', notes: '',
};

const grossCost = (net, vat) => {
  const n = parseFloat(net);
  const v = parseFloat(vat);
  if (isNaN(n) || isNaN(v)) return null;
  return n * (1 + v / 100);
};

export default function Stock() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const [s, sup] = await Promise.all([
      supabase.from('stock').select('*').order('name'),
      supabase.from('suppliers').select('id, name').order('name'),
    ]);
    setItems(s.data || []);
    setSuppliers(sup.data || []);
  };

  const filtered = items.filter(i => {
    const matchSearch = `${i.name} ${i.supplier} ${i.category}`.toLowerCase().includes(search.toLowerCase());
    const matchCat = !catFilter || i.category === catFilter;
    return matchSearch && matchCat;
  });

  const isLow = (i) => i.minQty && parseFloat(i.currentQty) <= parseFloat(i.minQty);
  const isExpiring = (i) => {
    if (!i.expiryDate) return false;
    const days = (new Date(i.expiryDate) - Date.now()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days < 90;
  };

  const saveNew = async () => {
    if (!form.name) return;
    await supabase.from('stock').insert([form]);
    setForm(empty); setShowNew(false); load();
  };

  const saveEdit = async () => {
    const { id, created_at, updated_at, ...rest } = editing;
    await supabase.from('stock').update({ ...rest, updated_at: new Date().toISOString() }).eq('id', id);
    setEditing(null); load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this stock item?')) return;
    await supabase.from('stock').delete().eq('id', id);
    setExpanded(null); load();
  };

  const Form = ({ data, setData, onSave, onCancel }) => {
    const gross = grossCost(data.costPerUnit, data.vatRate);
    return (
      <div className="patient-form">
        <div className="form-grid">
          <div className="form-field full"><label>Item Name *</label><input value={data.name} onChange={e => setData({ ...data, name: e.target.value })} /></div>
          <div className="form-field">
            <label>Category</label>
            <select value={data.category} onChange={e => setData({ ...data, category: e.target.value })}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Supplier</label>
            <select value={data.supplier} onChange={e => setData({ ...data, supplier: e.target.value })}>
              <option value="">Select supplier...</option>
              {suppliers.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div className="form-field"><label>Account No.</label><input value={data.accountNo} onChange={e => setData({ ...data, accountNo: e.target.value })} /></div>
          <div className="form-field"><label>Unit (e.g. box, bottle)</label><input value={data.unit} onChange={e => setData({ ...data, unit: e.target.value })} /></div>

          {/* Cost fields */}
          <div className="form-field">
            <label>Net Cost per Unit £ (ex VAT)</label>
            <input type="number" step="0.0001" value={data.costPerUnit}
              onChange={e => setData({ ...data, costPerUnit: e.target.value })} />
          </div>
          <div className="form-field">
            <label>VAT Rate</label>
            <select value={data.vatRate} onChange={e => setData({ ...data, vatRate: e.target.value })}>
              {VAT_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Gross Cost per Unit £ (inc VAT)</label>
            <input
              value={gross !== null ? gross.toFixed(4) : ''}
              readOnly
              style={{ background: 'var(--cream-dark)', color: 'var(--soft)', cursor: 'default' }}
            />
          </div>

          {/* Stock levels */}
          <div className="form-field"><label>Current Stock</label><input type="number" step="0.01" value={data.currentQty} onChange={e => setData({ ...data, currentQty: e.target.value })} /></div>
          <div className="form-field"><label>Reorder at (Min Level)</label><input type="number" step="0.01" value={data.minQty} onChange={e => setData({ ...data, minQty: e.target.value })} /></div>
          <div className="form-field"><label>Expiry Date</label><input type="date" value={data.expiryDate} onChange={e => setData({ ...data, expiryDate: e.target.value })} /></div>
          <div className="form-field full"><label>Notes</label><textarea rows={2} value={data.notes} onChange={e => setData({ ...data, notes: e.target.value })} /></div>
        </div>
        <div className="form-actions">
          <button className="btn-ghost" onClick={onCancel}><X size={14} /> Cancel</button>
          <button className="btn-gold" onClick={onSave}><Save size={14} /> Save</button>
        </div>
      </div>
    );
  };

  const lowCount = items.filter(isLow).length;
  const expiringCount = items.filter(isExpiring).length;

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Stock</h2>
          <p className="section-sub">
            {items.length} items
            {lowCount > 0 && <span className="alert-badge" style={{ marginLeft: 8 }}>{lowCount} low</span>}
            {expiringCount > 0 && <span className="alert-badge amber" style={{ marginLeft: 6 }}>{expiringCount} expiring</span>}
          </p>
        </div>
        <button className="btn-gold" onClick={() => { setShowNew(true); setExpanded(null); }}>
          <Plus size={14} /> Add Item
        </button>
      </div>

      <div className="search-bar">
        <Search size={15} className="search-icon" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search stock..." className="search-input" />
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="filter-select">
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {showNew && (
        <div className="patient-card expanded" style={{ marginBottom: '1rem' }}>
          <div className="card-header">
            <div className="patient-name-block">
              <div className="patient-initials">+</div>
              <div><div className="patient-name">New Stock Item</div></div>
            </div>
          </div>
          <Form data={form} setData={setForm} onSave={saveNew} onCancel={() => { setShowNew(false); setForm(empty); }} />
        </div>
      )}

      <div className="patient-list">
        {/* Column headers */}
        <div className="stock-list-header" style={{ gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1fr 1fr 80px' }}>
          <span>Item</span>
          <span>Category</span>
          <span>Net Cost</span>
          <span>VAT</span>
          <span>Gross Cost</span>
          <span>Stock / Min</span>
          <span></span>
        </div>

        {filtered.map(item => {
          const gross = grossCost(item.costPerUnit, item.vatRate);
          const low = isLow(item);
          const exp = isExpiring(item);
          return (
            <div key={item.id} className={`patient-card ${expanded === item.id ? 'expanded' : ''} ${low ? 'low-stock-card' : ''}`}>
              <div className="card-header" onClick={() => { setExpanded(expanded === item.id ? null : item.id); setEditing(null); }}>
                <div className="patient-name-block" style={{ flex: 1 }}>
                  <div style={{ flex: 1 }}>
                    <div className="patient-name">
                      {item.name}
                      {low && <AlertCircle size={13} style={{ color: 'var(--red)', marginLeft: 6, verticalAlign: 'middle' }} />}
                      {exp && <span className="mini-tag" style={{ background: 'rgba(196,134,26,0.15)', color: 'var(--amber)' }}>Exp soon</span>}
                    </div>
                    <div className="patient-meta">
                      <span className="mini-tag">{item.category}</span>
                      {item.supplier && <span>{item.supplier}</span>}
                      {item.unit && <span>per {item.unit}</span>}
                    </div>
                  </div>

                  {/* Inline cost summary */}
                  <div className="stock-cost-row">
                    <div className="stock-cost-cell">
                      <span className="stock-cost-label">Net</span>
                      <span>{item.costPerUnit ? `£${parseFloat(item.costPerUnit).toFixed(4)}` : '—'}</span>
                    </div>
                    <div className="stock-cost-cell">
                      <span className="stock-cost-label">VAT</span>
                      <span>{item.vatRate || '20'}%</span>
                    </div>
                    <div className="stock-cost-cell">
                      <span className="stock-cost-label">Gross</span>
                      <span className="cost-gross">{gross !== null ? `£${gross.toFixed(4)}` : '—'}</span>
                    </div>
                    <div className="stock-cost-cell">
                      <span className="stock-cost-label">Stock</span>
                      <span className={low ? 'cost-low' : ''}>{item.currentQty || '—'}{item.minQty ? ` / ${item.minQty}` : ''}</span>
                    </div>
                  </div>
                </div>

                <div className="card-actions" onClick={e => e.stopPropagation()}>
                  <button className="icon-btn" onClick={() => { setEditing(item); setExpanded(item.id); }}><Edit3 size={14} /></button>
                  <button className="icon-btn danger" onClick={() => del(item.id)}><Trash2 size={14} /></button>
                  <span className="expand-icon">{expanded === item.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
                </div>
              </div>

              {expanded === item.id && (
                <div className="card-body">
                  {editing?.id === item.id ? (
                    <Form data={editing} setData={setEditing} onSave={saveEdit} onCancel={() => setEditing(null)} />
                  ) : (
                    <div className="detail-grid">
                      <div className="detail-row"><span className="detail-label">Net Cost (ex VAT)</span><span>{item.costPerUnit ? `£${parseFloat(item.costPerUnit).toFixed(4)}` : '—'}</span></div>
                      <div className="detail-row"><span className="detail-label">VAT Rate</span><span>{item.vatRate || '20'}%</span></div>
                      <div className="detail-row"><span className="detail-label">Gross Cost (inc VAT)</span><span className="cost-gross">{gross !== null ? `£${gross.toFixed(4)}` : '—'}</span></div>
                      <div className="detail-row"><span className="detail-label">Current Stock</span><span className={low ? 'cost-low' : ''}>{item.currentQty || '—'} {item.unit}</span></div>
                      <div className="detail-row"><span className="detail-label">Reorder Level</span><span>{item.minQty || '—'} {item.unit}</span></div>
                      {item.expiryDate && <div className="detail-row"><span className="detail-label">Expiry</span><span className={exp ? 'cost-low' : ''}>{new Date(item.expiryDate).toLocaleDateString('en-GB')}</span></div>}
                      {item.supplier && <div className="detail-row"><span className="detail-label">Supplier</span><span>{item.supplier}</span></div>}
                      {item.accountNo && <div className="detail-row"><span className="detail-label">Account No.</span><span>{item.accountNo}</span></div>}
                      {item.notes && <div className="detail-row full"><span className="detail-label">Notes</span><span>{item.notes}</span></div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && !showNew && (
          <div className="empty-state">
            <p>No stock items{catFilter ? ` in ${catFilter}` : ''}</p>
            <button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> Add first item</button>
          </div>
        )}
      </div>
    </div>
  );
}
